/*
 * NC Hero · Carrusel — anillo de slides sin clones, swipe, autoplay y video controlados.
 * Cada movimiento coloca la slide entrante fuera de pantalla en el lado correcto (sin transición) y luego anima
 * entrante→0 / saliente→−d, así 1→2→3→1 y 1←3 son continuos y no hay contenido duplicado ni IDs repetidos.
 *
 * Autoplay (duración por tipo de slide):
 *   · slide de IMAGEN → temporizador configurado (data-delay, p. ej. 6 s).
 *   · slide de VIDEO  → sin temporizador: se reproduce el CLIP [inicio, fin] definido en el Theme Editor
 *     (data-start / data-end, en segundos) y el carrusel avanza al terminar el clip (fin configurado) o el video
 *     (`ended`). Sin loop nativo. Al volver a ese slide siempre empieza en `inicio`.
 *   · Validación del recorte (sin errores JS): inicio < 0 → 0; inicio ≥ duración → 0; fin ≤ inicio → se ignora (final
 *     natural); fin > duración → duración. Fin = 0 → final natural.
 *   · Si el video no puede reproducirse (reduce movimiento, ahorro de datos, autoplay bloqueado, error o no arranca en
 *     10 s) el slide usa el temporizador.
 *   · Pausas: hover / foco por teclado / interacción / pestaña oculta / hero fuera de pantalla. NO hay botón visible de
 *     play/pause. Al salir manualmente el video se pausa. Siempre muted + playsinline; solo la slide activa reproduce.
 */
if (!customElements.get('nc-hero')) {
  customElements.define('nc-hero', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.viewport = this.querySelector('[data-nch-viewport]');
      this.slides = [...this.querySelectorAll('[data-nch-slide]')];
      this.n = this.slides.length;
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.dots = [...this.querySelectorAll('[data-nch-dot]')];
      this.counter = this.querySelector('[data-nch-current]');
      this.status = this.querySelector('[data-nch-status]');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.saveData = !!(navigator.connection && navigator.connection.saveData);
      this.autoplay = this.dataset.autoplay === 'true';
      this.delay = (parseInt(this.dataset.delay, 10) || 6) * 1000;
      this.flags = new Set();
      if (document.hidden) this.flags.add('hidden');
      this.userPaused = false; // solo lo activa el Theme Editor al seleccionar un banner
      this.busy = false;
      this.timer = 0;
      this.busyTimer = 0;
      this.videoGuard = 0;
      this.trimTimer = 0;
      this.failed = new Set();
      this.restart = new Set();
      this.abort = new AbortController();
      const { signal } = this.abort;
      this.armed = false;
      this.videos = this.slides.map((s) => s.querySelector('[data-nch-video]'));
      this.hasVideo = this.videos.some(Boolean);

      // Los videos participan en el autoplay: sin loop (el fin del clip avanza el carrusel). Si el carrusel no avanza solo,
      // el video se repite dentro de su clip.
      const drives = this.autoplay && this.n > 1;
      this.videos.forEach((video, i) => {
        if (!video) return;
        video.muted = true;
        video.loop = false;
        video.removeAttribute('loop');
        this.restart.add(i);
        video.addEventListener('ended', () => this.onVideoEnded(i), { signal });
        video.addEventListener('error', () => {
          this.failed.add(i);
          if (i === this.index) this.schedule();
        }, { signal });
        video.addEventListener('timeupdate', () => this.onVideoTime(i, video), { signal });
        video.addEventListener('playing', () => this.armTrimTimer(i, video), { signal });
        video.addEventListener('pause', () => clearTimeout(this.trimTimer), { signal });
      });
      this.drives = drives;

      if (this.n > 1) {
        this.querySelector('[data-nch-prev]')?.addEventListener('click', () => this.step(-1, true), { signal });
        this.querySelector('[data-nch-next]')?.addEventListener('click', () => this.step(1, true), { signal });
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.index), true), { signal }));
        // Teclado: las flechas solo actúan cuando el foco está en los controles del carrusel (no es un atajo global).
        this.addEventListener('keydown', (event) => {
          if (!event.target.closest('[data-nch-controls]')) return;
          if (event.key === 'ArrowLeft') { event.preventDefault(); this.step(-1, true); }
          if (event.key === 'ArrowRight') { event.preventDefault(); this.step(1, true); }
        }, { signal });
        this.bindDrag(signal);
        this.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') this.setFlag('hover', true); }, { signal });
        this.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse') this.setFlag('hover', false); }, { signal });
        this.addEventListener('focusin', (event) => { if (event.target.matches(':focus-visible')) this.setFlag('focus', true); }, { signal });
        this.addEventListener('focusout', () => this.setFlag('focus', false), { signal });
      }
      document.addEventListener('visibilitychange', () => { this.setFlag('hidden', document.hidden); this.syncMedia(); }, { signal });
      this.reduced.addEventListener('change', () => { this.schedule(); this.syncMedia(); }, { signal });
      const armLater = () => (window.requestIdleCallback ? requestIdleCallback(() => this.arm(), { timeout: 2500 }) : setTimeout(() => this.arm(), 400));
      if (document.readyState === 'complete') armLater(); else window.addEventListener('load', armLater, { once: true, signal });
      // Theme Editor: al seleccionar un bloque se muestra ese banner.
      document.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target);
        if (i < 0) return;
        this.arm();
        this.userPaused = true;
        this.schedule();
        this.busy = false;
        this.goTo(i, false);
      }, { signal });
      document.addEventListener('shopify:block:deselect', () => { this.userPaused = false; this.schedule(); }, { signal });
      this.observer = new IntersectionObserver((entries) => {
        this.setFlag('offscreen', !entries[0].isIntersecting);
        this.syncMedia();
      }, { threshold: 0.3 });
      this.observer.observe(this);
      this.syncMedia();
      this.schedule();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      clearTimeout(this.timer);
      clearTimeout(this.busyTimer);
      clearTimeout(this.videoGuard);
      clearTimeout(this.trimTimer);
      this.slides?.forEach((s) => s.querySelector('[data-nch-video]')?.pause());
      this.ready = false;
    }

    arm() {
      if (this.armed) return;
      this.armed = true;
      this.slides.forEach((slide) => slide.setAttribute('data-nch-armed', ''));
    }

    mod(i) { return (i + this.n) % this.n; }

    setP(slide, p) { slide.style.setProperty('--p', p); }

    // Coloca la slide fuera de pantalla (lado `p`) SIN transición, para que entre con movimiento continuo.
    jump(slide, p) {
      slide.style.transition = 'none';
      this.setP(slide, p);
      slide.style.removeProperty('--drag');
      void slide.offsetWidth;
      slide.style.transition = '';
    }

    durationMs() {
      const raw = getComputedStyle(this.slides[0]).transitionDuration || '0s';
      const first = raw.split(',')[0].trim();
      return (first.endsWith('ms') ? parseFloat(first) : parseFloat(first) * 1000) || 0;
    }

    step(dir, manual) {
      this.goTo(this.mod(this.index + dir), manual, dir);
    }

    goTo(to, manual, dir) {
      this.arm();
      if (this.busy || to === this.index || this.n < 2) return;
      const from = this.index;
      let d = dir;
      if (!d) d = this.mod(to - from) <= this.n / 2 ? 1 : -1;
      const enter = this.slides[to];
      const leave = this.slides[from];
      this.jump(enter, d);
      this.commit(from, to, d, manual);
      this.setP(enter, 0);
      this.setP(leave, -d);
    }

    // Actualiza estado/aria/media tras decidir el cambio (usado por botones, puntos, autoplay y swipe).
    commit(from, to, d, manual) {
      this.index = to;
      this.busy = true;
      clearTimeout(this.busyTimer);
      clearTimeout(this.trimTimer);
      // El video del slide entrante siempre arranca en `inicio` (también al volver después de una vuelta del carrusel).
      const entering = this.videos[to];
      if (entering) {
        this.failed.delete(to);
        this.restart.add(to);
        entering._done = false;
      }
      this.paint();
      this.syncMedia();
      if (manual && this.status) this.status.textContent = this.dataset.labelSlide.replace('[n]', to + 1).replace('[total]', this.n);
      this.busyTimer = setTimeout(() => {
        this.busy = false;
        this.videos[from]?.pause();
      }, this.durationMs() + 40);
      this.schedule();
    }

    paint() {
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        if (active) { slide.removeAttribute('aria-hidden'); slide.inert = false; }
        else { slide.setAttribute('aria-hidden', 'true'); slide.inert = true; }
      });
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === this.index);
        if (i === this.index) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(this.index + 1).padStart(2, '0');
    }

    // ---- Video: solo la slide activa (y visible) se reproduce ----
    syncMedia() {
      const allow = !this.reduced.matches && !this.saveData && !document.hidden && !this.flags.has('offscreen') && !this.userPaused;
      this.slides.forEach((slide, i) => {
        const video = this.videos[i];
        if (!video) return;
        if (i === this.index && allow) this.playVideo(slide, video, i);
        else if (i === this.index) video.pause();
        else if (!this.busy) video.pause();
      });
    }

    // Recorte validado. `s` = inicio, `e` = fin (0 = final natural). Nunca lanza.
    trimOf(video) {
      const dur = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0;
      let s = parseFloat(String(video.dataset.start || '0').replace(',', '.'));
      let e = parseFloat(String(video.dataset.end || '0').replace(',', '.'));
      if (!Number.isFinite(s) || s < 0) s = 0;
      if (!Number.isFinite(e) || e < 0) e = 0;
      if (dur) {
        if (s >= dur) s = 0;
        if (e > dur) e = dur;
      }
      if (!(e > s)) e = 0;
      return { s, e };
    }

    async playVideo(slide, video, i) {
      try {
        if (!video.getAttribute('src')) {
          const mobile = matchMedia('(max-width: 749px)').matches;
          video.src = (mobile ? video.dataset.srcM : video.dataset.srcD) || video.dataset.srcD;
          video.muted = true;
          video.load();
        }
        // Seguro: si en 10 s el video no arrancó, el slide pasa al temporizador (no se queda esperando un fin que no llega).
        clearTimeout(this.videoGuard);
        this.videoGuard = setTimeout(() => {
          if (i === this.index && video.currentTime <= (this.trimOf(video).s || 0) + 0.1 && !video._done) {
            this.failed.add(i);
            this.schedule();
          }
        }, 10000);
        // Metadatos necesarios para conocer la duración y poder posicionar el inicio del clip.
        if (video.readyState < 1) {
          await new Promise((resolve, reject) => {
            const ok = () => { video.removeEventListener('error', bad); resolve(); };
            const bad = () => { video.removeEventListener('loadedmetadata', ok); reject(new Error('metadata')); };
            video.addEventListener('loadedmetadata', ok, { once: true });
            video.addEventListener('error', bad, { once: true });
          });
          if (i !== this.index) return;
        }
        const { s, e } = this.trimOf(video);
        if (this.restart.has(i) || video.ended || (e && video.currentTime >= e)) {
          this.restart.delete(i);
          video._done = false;
          video.currentTime = s;
        }
        await video.play();
        slide.classList.add('is-video-playing');
      } catch (error) {
        // Autoplay bloqueado / error: se queda la imagen y el slide usa el temporizador (fallback silencioso).
        slide.classList.remove('is-video-playing');
        this.failed.add(i);
        if (i === this.index) this.schedule();
      }
    }

    // ¿El slide activo avanza cuando termina su clip (en vez de por temporizador)?
    videoDrives() {
      const video = this.videos[this.index];
      return Boolean(video) && !this.failed.has(this.index) && !this.reduced.matches && !this.saveData;
    }

    videoFinished(video) {
      return Boolean(video && (video.ended || video._done));
    }

    // Fin de clip por tiempo: pausa y avanza igual que `ended`.
    finishClip(i, video) {
      if (video._done) return;
      video._done = true;
      video.pause();
      this.onVideoEnded(i);
    }

    onVideoEnded(i) {
      const video = this.videos[i];
      if (video) video._done = true;
      if (i !== this.index) return;
      if (!this.autoplay || this.n < 2) return;
      // Con hover/foco/edición no se avanza; al liberarse, schedule() detecta el clip terminado.
      if (this.flags.size || this.userPaused) return;
      this.step(1, false);
    }

    // Temporizador preciso al fin del clip (timeupdate solo llega cada ~250 ms).
    armTrimTimer(i, video) {
      clearTimeout(this.trimTimer);
      if (i !== this.index || !this.autoplay || this.n < 2) return;
      const { e } = this.trimOf(video);
      if (!e) return;
      const remaining = Math.max(0, (e - video.currentTime) / (video.playbackRate || 1)) * 1000;
      this.trimTimer = setTimeout(() => {
        if (i === this.index && !video.paused) this.finishClip(i, video);
      }, remaining + 5);
    }

    onVideoTime(i, video) {
      if (i !== this.index) return;
      const { s, e } = this.trimOf(video);
      const end = e || video.duration;
      if (this.dots[i] && Number.isFinite(end) && end > s) {
        const p = Math.min(1, Math.max(0, (video.currentTime - s) / (end - s)));
        this.dots[i].style.setProperty('--vp', p.toFixed(3));
      }
      if (e && video.currentTime >= e && this.autoplay && this.n > 1) this.finishClip(i, video);
    }

    // ---- Autoplay ----
    setFlag(name, on) {
      if (on) this.flags.add(name); else this.flags.delete(name);
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      this.dots.forEach((dot) => { dot.classList.remove('is-running', 'is-video'); dot.style.removeProperty('--vp'); });
      if (!this.autoplay || this.n < 2 || this.reduced.matches || this.flags.size || this.userPaused) return;
      if (this.videoDrives()) {
        // El clip manda: sin temporizador. El punto activo muestra el progreso real del clip.
        const video = this.videos[this.index];
        this.dots[this.index]?.classList.add('is-video');
        if (this.videoFinished(video)) this.timer = setTimeout(() => this.step(1, false), 0);
        else this.onVideoTime(this.index, video);
        return;
      }
      void this.offsetWidth;
      this.dots[this.index]?.classList.add('is-running');
      this.timer = setTimeout(() => this.step(1, false), this.delay);
    }

    // ---- Swipe / arrastre ----
    bindDrag(signal) {
      let start = null;
      let dragging = false;
      let dir = 0;
      let neighbor = -1;
      let moved = false;
      const width = () => this.viewport.clientWidth || 1;

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.button !== 0 || this.busy) return;
        this.arm();
        start = { x: event.clientX, y: event.clientY, t: performance.now(), id: event.pointerId, lastX: event.clientX, lastT: performance.now(), v: 0 };
        moved = false;
      }, { signal });

      this.viewport.addEventListener('pointermove', (event) => {
        if (!start || event.pointerId !== start.id) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (!dragging) {
          if (Math.abs(dx) < 8 || Math.abs(dx) < Math.abs(dy)) return;
          dragging = true;
          moved = true;
          dir = dx < 0 ? 1 : -1;
          neighbor = this.mod(this.index + dir);
          try { this.viewport.setPointerCapture(event.pointerId); } catch (error) { /* puntero sintético o ya liberado */ }
          this.classList.add('is-dragging');
          this.jump(this.slides[neighbor], dir);
          this.setFlag('drag', true);
        }
        const clamped = dir > 0 ? Math.min(0, dx) : Math.max(0, dx);
        const now = performance.now();
        start.v = (event.clientX - start.lastX) / Math.max(1, now - start.lastT);
        start.lastX = event.clientX;
        start.lastT = now;
        this.slides[this.index].style.setProperty('--drag', clamped + 'px');
        this.slides[neighbor].style.setProperty('--drag', clamped + 'px');
      }, { signal });

      const end = (event) => {
        if (!start || (event && event.pointerId !== start.id)) return;
        const wasDragging = dragging;
        const s = start;
        start = null;
        dragging = false;
        if (!wasDragging) return;
        const dx = (s.lastX - s.x);
        const progress = Math.abs(dx) / width();
        const fast = Math.abs(s.v) > 0.45 && Math.sign(s.v) === Math.sign(dx);
        const current = this.slides[this.index];
        const next = this.slides[neighbor];
        // Se reactivan las transiciones partiendo de la posición arrastrada.
        void current.offsetWidth;
        this.classList.remove('is-dragging');
        void current.offsetWidth;
        current.style.removeProperty('--drag');
        next.style.removeProperty('--drag');
        if (progress > 0.2 || fast) {
          const from = this.index;
          this.commit(from, neighbor, dir, true);
          this.setP(next, 0);
          this.setP(current, -dir);
        } else {
          this.setP(current, 0);
          this.setP(next, dir);
        }
        this.setFlag('drag', false);
        this.schedule();
      };
      this.viewport.addEventListener('pointerup', end, { signal });
      this.viewport.addEventListener('pointercancel', end, { signal });
      // Tras un arrastre no se dispara el clic del enlace sobre el que terminó el gesto.
      this.viewport.addEventListener('click', (event) => {
        if (moved) { event.preventDefault(); event.stopPropagation(); moved = false; }
      }, { capture: true, signal });
    }
  });
}
